/**
 * useAutoDetect — capture frames from the seller's live camera and ask the
 * backend ("/streams/{id}/detect-item") if there's a sellable item in view.
 *
 * Design notes
 * - Only the SELLER on their OWN live stream gets to run this. Hard-gated
 *   at the hook level so a buyer rendering the same page can never trip
 *   the LLM bill or the camera permission.
 * - We snapshot a frame every `INTERVAL_MS` (default 2.5s). Each frame is
 *   downscaled to 720px wide and JPEG-encoded at 0.7 quality — about
 *   ~60-90 KB on the wire.
 * - De-duplication: once a candidate is shown (or dismissed), its
 *   `item_signature` is muted for `MUTE_MS` (default 60s).
 * - The hook never auto-creates listings. It just surfaces a candidate.
 *
 * Inputs:
 *   streamId          string — id of the live stream
 *   videoEl           HTMLVideoElement | null — the seller's local <video>
 *   enabled           bool — master kill-switch (rail toggle)
 *   onCandidate       (candidate) => void — called when a new item is ready
 *   onError           ({status, detail}) => void — first error in a session
 *   onDiag            ({code, detail}) => void — diagnostic hooks for the
 *                     parent so we can surface "no frames yet…" toasts when
 *                     the loop is alive but the video element isn't yielding
 *                     pixels. Fires AT MOST once per session per code.
 *   onFirstSuccess    (result) => void — first successful API round-trip
 *                     after enable. Lets the parent flash a "Detect armed"
 *                     toast so the seller knows the loop is firing.
 *
 * Returns:
 *   { active, lastStatus, mute }
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const INTERVAL_MS = 2500;
const MUTE_MS = 60_000;
const MIN_CONFIDENCE = 55;
const MAX_WIDTH = 720;
// After this many consecutive "no_frame" ticks (~20 seconds with the
// default 2.5s interval) the hook fires a one-shot diagnostic so the
// seller knows the camera isn't producing pixels we can grab. Without
// this the rail just sits there silently when, say, the video element
// is mounted but `videoWidth` never climbs above 0.
const NO_FRAME_WARN_AFTER = 8;

function snapshotJpeg(videoEl) {
  // No video, no frame. Returning null skips this tick gracefully.
  if (!videoEl) return Promise.resolve({ blob: null, reason: "no-element" });
  if (!videoEl.videoWidth || !videoEl.videoHeight) {
    return Promise.resolve({ blob: null, reason: "zero-dim" });
  }
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  const scale = Math.min(1, MAX_WIDTH / vw);
  const w = Math.max(1, Math.round(vw * scale));
  const h = Math.max(1, Math.round(vh * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve({ blob: null, reason: "no-ctx" });
  try {
    ctx.drawImage(videoEl, 0, 0, w, h);
  } catch (e) {
    // SecurityError (cross-origin tainted) or InvalidStateError land here.
    // We surface this so the parent can show a real toast.
    return Promise.resolve({
      blob: null,
      reason: "draw-error",
      detail: e && e.name ? `${e.name}: ${e.message || ""}` : String(e),
    });
  }
  return new Promise((resolve) => {
    try {
      canvas.toBlob(
        (b) => {
          if (!b) {
            resolve({ blob: null, reason: "encode-null" });
          } else {
            resolve({ blob: b, reason: "ok" });
          }
        },
        "image/jpeg",
        0.7,
      );
    } catch (e) {
      resolve({
        blob: null,
        reason: "encode-error",
        detail: e && e.name ? `${e.name}: ${e.message || ""}` : String(e),
      });
    }
  });
}

export function useAutoDetect({ streamId, videoEl, enabled, onCandidate, onError, onDiag, onFirstSuccess }) {
  const [active, setActive] = useState(false);
  const [lastStatus, setLastStatus] = useState(null);
  const mutedRef = useRef(new Map()); // signature → timestamp (ms)
  const busyRef = useRef(false);
  const cbRef = useRef(onCandidate);
  const errCbRef = useRef(onError);
  const diagCbRef = useRef(onDiag);
  const firstOkCbRef = useRef(onFirstSuccess);
  cbRef.current = onCandidate;
  errCbRef.current = onError;
  diagCbRef.current = onDiag;
  firstOkCbRef.current = onFirstSuccess;

  useEffect(() => {
    if (!enabled || !streamId || !videoEl) {
      setActive(false);
      // Console signal so we can see WHY the loop is idle from devtools
      // without a custom build.  This is the single biggest reason the
      // user has been reporting "nothing happens" on prod — the gate
      // closes silently because one of the three inputs is falsy.
      // eslint-disable-next-line no-console
      console.info(
        "[auto-detect] loop idle",
        { enabled, streamId: !!streamId, hasVideoEl: !!videoEl },
      );
      return undefined;
    }
    setActive(true);
    let alive = true;
    let timer = null;
    let consecutiveNoFrame = 0;
    let firedFirstOk = false;
    const diagFired = new Set();

    const fireDiag = (code, detail) => {
      if (diagFired.has(code)) return;
      diagFired.add(code);
      try { diagCbRef.current?.({ code, detail }); } catch (_) {}
    };

    // eslint-disable-next-line no-console
    console.info("[auto-detect] loop armed", {
      streamId,
      videoWidth: videoEl.videoWidth,
      videoHeight: videoEl.videoHeight,
      readyState: videoEl.readyState,
    });

    const tick = async () => {
      if (!alive) return;
      if (busyRef.current) { schedule(); return; }
      busyRef.current = true;
      try {
        const snap = await snapshotJpeg(videoEl);
        if (!snap.blob) {
          consecutiveNoFrame += 1;
          setLastStatus({ kind: "no_frame", reason: snap.reason, ts: Date.now() });
          // eslint-disable-next-line no-console
          console.debug("[auto-detect] no frame", snap);
          if (snap.reason === "draw-error" || snap.reason === "encode-error") {
            // Hard browser-side failure — surface immediately.
            fireDiag(snap.reason, snap.detail);
          } else if (consecutiveNoFrame === NO_FRAME_WARN_AFTER) {
            fireDiag("no-frames", snap.reason || "video not producing frames");
          }
          schedule();
          return;
        }
        consecutiveNoFrame = 0;
        const form = new FormData();
        form.append("file", snap.blob, "frame.jpg");
        // eslint-disable-next-line no-console
        console.debug("[auto-detect] sending frame", { bytes: snap.blob.size });
        const r = await api.post(`/streams/${streamId}/detect-item`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 15000,
        });
        const d = r?.data;
        setLastStatus({
          kind: "ok",
          confidence: d?.confidence ?? 0,
          is_sellable: !!d?.is_sellable,
          title: d?.title || null,
          ts: Date.now(),
        });
        // eslint-disable-next-line no-console
        console.debug("[auto-detect] response", d);
        if (!firedFirstOk) {
          firedFirstOk = true;
          try { firstOkCbRef.current?.(d); } catch (_) {}
        }
        if (
          alive &&
          d?.is_sellable &&
          d.confidence >= MIN_CONFIDENCE &&
          d.title &&
          d.suggested_price > 0
        ) {
          const now = Date.now();
          const sig = (d.item_signature || d.title).toLowerCase();
          const mutedAt = mutedRef.current.get(sig) || 0;
          if (now - mutedAt > MUTE_MS) {
            mutedRef.current.set(sig, now);
            cbRef.current?.(d);
          }
        }
      } catch (e) {
        // Don't auto-spam the UI with toasts, but DO surface the first
        // failure so the seller has a clue when nothing's happening.
        // Common cases: 402 (not yearly Plus), 429 (rate limit), 502
        // (LLM hiccup), network timeout.
        const status = e?.response?.status;
        const detail =
          e?.response?.data?.detail?.message ||
          (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null) ||
          e?.message ||
          "Detect failed";
        setLastStatus({ kind: "error", status, detail, ts: Date.now() });
        // eslint-disable-next-line no-console
        console.warn("[auto-detect] error", { status, detail });
        try { errCbRef.current?.({ status, detail }); } catch (_) {}
      } finally {
        busyRef.current = false;
        schedule();
      }
    };

    const schedule = () => {
      if (!alive) return;
      timer = setTimeout(tick, INTERVAL_MS);
    };

    schedule();

    return () => {
      alive = false;
      setActive(false);
      if (timer) clearTimeout(timer);
    };
  }, [enabled, streamId, videoEl]);

  return {
    active,
    lastStatus,
    mute: (sig) => {
      if (sig) mutedRef.current.set(sig.toLowerCase(), Date.now());
    },
  };
}
